import type { FC, ReactNode } from 'react'
import type { Locale } from '@/types'

import { useEffect, useMemo, useState } from 'react'
import { Select } from '@/components/ui/field'
import { COMMON_COUNTRIES, countryName, sortedCountries } from '@/lib/countries'

interface CountrySelectProps {
    id: string
    name?: string
    value: string
    onChange: (value: string) => void
    locale: Locale
    placeholder: string
    commonLabel: string
    allLabel: string
    invalid?: boolean
    required?: boolean
    disabled?: boolean
}

export const CountrySelect: FC<CountrySelectProps> = ({
    id,
    name,
    value,
    onChange,
    locale,
    placeholder,
    commonLabel,
    allLabel,
    invalid,
    required,
    disabled
}): ReactNode => {
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    const countries = useMemo(() => (mounted ? sortedCountries(locale) : []), [mounted, locale])

    return (
        <Select
            id={id}
            name={name}
            value={value}
            onChange={event => onChange(event.target.value)}
            invalid={invalid}
            required={required}
            disabled={disabled}
            autoComplete='country'
        >
            <option value=''>{placeholder}</option>

            {!mounted && value && <option value={value}>{value}</option>}

            {mounted && (
                <>
                    <optgroup label={commonLabel}>
                        {COMMON_COUNTRIES.map(code => (
                            <option key={`common-${code}`} value={code}>{countryName(code, locale)}</option>
                        ))}
                    </optgroup>

                    <optgroup label={allLabel}>
                        {countries.map(country => (
                            <option key={country.code} value={country.code}>{country.name}</option>
                        ))}
                    </optgroup>
                </>
            )}
        </Select>
    )
}
